import React, { useState, useEffect } from 'react';
import {
    Sidebar, SidebarNav, SidebarLayout,
    Container, RowView, Col, Navbar, Heading, Paragraph,
    Button, CardView, Image, Input, Spacer,
    Alert, Badge, ListView, ListItem, Flex, Grid,
    Table, Progress, Spinner, Dropdown, Link,
    Modal, Tabs, Divider, Text,
    useResponsive, MobileOnly, DesktopOnly
} from './index';
import { Nav } from 'react-bootstrap';
import './MainComponents.css';
import ImageShow from '../../Uitiles/ImageShow';
import { useSettings } from '../../context/SettingsContext';
import useOs from '../../hooks/Os';

const DashboardExample = () => {
    const [showModal, setShowModal] = useState(false);
    const [selectedPage, setSelectedPage] = useState('dashboard');
    const [notifications, setNotifications] = useState(5);
    const [isAtTop, setIsAtTop] = useState(true);  // State to track if we're at the top
    const platformInfo = useOs();
    const [showSidebar, setShowSidebar] = useState(false);
    // Use responsive hook
    const screen = useResponsive();
    const { settings } = useSettings();

    // Initialize platform on mount (works even without Capacitor installed)
    React.useEffect(() => {
        const initPlatform = async () => {
            try {
                const { initializeCapacitor } = await import('./index');
                await initializeCapacitor();
            } catch (error) {
                console.log('Running without Capacitor:', error.message);
            }
        };
        initPlatform();
    }, []);

    // Sidebar navigation items
    const sidebarItems = [
        {
            icon: '🏠',
            label: 'Dashboard',
            key: 'dashboard',
            active: selectedPage === 'dashboard'
        },
        {
            icon: '📊',
            label: 'Analytics',
            key: 'analytics',
            badge: 'Pro',
            badgeColor: '#28a745'
        },
        {
            icon: '👥',
            label: 'Users',
            key: 'users',
            badge: '1.2k',
            badgeColor: '#007bff'
        },
        {
            icon: '📦',
            label: 'Products',
            key: 'products'
        },
        { divider: true },
        { header: 'Management' },
        {
            icon: '⚙️',
            label: 'Settings',
            key: 'settings'
        },
        {
            icon: '📝',
            label: 'Reports',
            key: 'reports'
        },
        {
            icon: '💬',
            label: 'Messages',
            key: 'messages',
            badge: notifications,
            badgeColor: '#dc3545'
        },
        { divider: true },
        {
            icon: '❓',
            label: 'Help & Support',
            key: 'help'
        }
    ];

    // Stats data
    const stats = [
        {
            title: 'Total Revenue',
            value: '$45,231',
            change: '+12.5%',
            changeType: 'positive',
            icon: '💰',
            bg: 'primary'
        },
        {
            title: 'New Users',
            value: '1,234',
            change: '+8.2%',
            changeType: 'positive',
            icon: '👥',
            bg: 'success'
        },
        {
            title: 'Orders',
            value: '892',
            change: '-3.1%',
            changeType: 'negative',
            icon: '📦',
            bg: 'warning'
        },
        {
            title: 'Conversion Rate',
            value: '3.45%',
            change: '+1.2%',
            changeType: 'positive',
            icon: '📈',
            bg: 'info'
        }
    ];

    // Recent orders data
    const recentOrders = [
        { id: '#12345', customer: 'John Doe', product: 'Product A', amount: '$125.00', status: 'Completed' },
        { id: '#12346', customer: 'Jane Smith', product: 'Product B', amount: '$89.50', status: 'Pending' },
        { id: '#12347', customer: 'Bob Johnson', product: 'Product C', amount: '$220.00', status: 'Completed' },
        { id: '#12348', customer: 'Alice Williams', product: 'Product D', amount: '$156.75', status: 'Processing' },
        { id: '#12349', customer: 'Charlie Brown', product: 'Product E', amount: '$99.00', status: 'Completed' }
    ];

    // Recent activities
    const activities = [
        { icon: '✅', text: 'New order received', time: '2 minutes ago', type: 'success' },
        { icon: '👤', text: 'New user registered', time: '15 minutes ago', type: 'info' },
        { icon: '📦', text: 'Product updated', time: '1 hour ago', type: 'primary' },
        { icon: '💬', text: 'New message from support', time: '2 hours ago', type: 'warning' },
        { icon: '🔔', text: 'System maintenance scheduled', time: '5 hours ago', type: 'danger' }
    ];

    const handleNavSelect = (key) => {
        setSelectedPage(key);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);  // Add scroll event listener
        return () => window.removeEventListener('scroll', handleScroll);  // Cleanup the event listener when the component is unmounted
    }, []);

    const handleScroll = () => {
        if (window.scrollY === 0) {
          setIsAtTop(true);  // If the scroll position is at the top, set state to true
        } else {
          setIsAtTop(false);  // If scrolled down, set state to false
        }
    };

    return (
        <SidebarLayout
            sidebarPosition="left"
            sidebarWidth="250px"
            sidebar={
                <Sidebar
                    position="left"
                    width="250px"
                    bg="#2c3e50"
                    fixed={true}
                    collapsible={true}
                    useMobileOffcanvas={true}
                    style={{ color: 'white' }}
                >
                    {/* Logo/Brand */}
                    <Flex justify="left" align="center" style={{ marginBottom: '30px' }}>
                        <Heading level={3} style={{ color: 'white', margin: 0 }}>
                            <ImageShow url={settings.footer_logo_url} width={150} />
                        </Heading>
                    </Flex>

                    {/* User Profile Card */}
                    <CardView
                        bg="dark"
                        text="white"
                        style={{ marginBottom: '20px', border: '1px solid #34495e' }}
                    >
                        <Flex align="center" gap="15px">
                            <Image
                                src="https://via.placeholder.com/50"
                                roundedCircle
                                width="50"
                                height="50"
                            />
                            <div>
                                <Text tag="div" style={{ fontWeight: 'bold', color: 'white' }}>
                                    Admin User
                                </Text>
                                <Text tag="small" style={{ color: '#95a5a6' }}>
                                    admin@myapp.com
                                </Text>
                            </div>
                        </Flex>
                    </CardView>

                    {/* Navigation */}
                    <SidebarNav
                        items={sidebarItems}
                        activeKey={selectedPage}
                        onSelect={handleNavSelect}
                    />

                    <Spacer height="20px" />

                    {/* Logout Button */}
                    <Button variant="outline-light" block style={{ marginTop: 'auto' }}>
                        🚪 Logout
                    </Button>
                </Sidebar>
            }
            navbar={
                <Navbar 
                    bg="white" 
                    variant="light" 
                    style={{ 
                        borderBottom: '1px solid #dee2e6', 
                        marginBottom: '20px', 
                        boxShadow: isAtTop ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                        borderRadius: isAtTop ? '5px' : '0',
                        height: isAtTop ? '60px' : '70px' 
                    }} 
                    className={`dc-navigationarea${isAtTop ? '' : ' stickymain'}`}
                    onClick={() => setShowSidebar(!showSidebar)}
                >
                    <Container fluid>
                        <Flex justify="space-between" align="center" style={{ width: '100%' }}>

                                <ImageShow
                                    src="https://via.placeholder.com/50"
                                    width="30"
                                    height="30"
                                    style={{ cursor: 'pointer' }}
                                />
                            <Heading level={4} style={{ margin: 0 }}>Dashboard</Heading>
                            
                            <Flex gap="15px" align="center">
                                {/* Notifications */}
                                <div style={{ position: 'relative' }}>
                                    <Button variant="outline-secondary">
                                        🔔
                                    </Button>
                                    {notifications > 0 && (
                                        <Badge
                                            bg="danger"
                                            pill
                                            style={{
                                                position: 'absolute',
                                                top: '-5px',
                                                right: '-5px',
                                                fontSize: '10px'
                                            }}
                                        >
                                            {notifications}
                                        </Badge>
                                    )}
                                </div>

                                {/* User Menu Dropdown */}
                                <Dropdown
                                    title="Profile"
                                    variant="outline-primary"
                                    items={[
                                        { label: 'My Profile', onClick: () => alert('Profile') },
                                        { label: 'Settings', onClick: () => alert('Settings') },
                                        { divider: true },
                                        { label: 'Logout', onClick: () => alert('Logout') }
                                    ]}
                                />
                            </Flex>
                        </Flex>
                    </Container>
                </Navbar>
            }
        >

            {/* Main Dashboard Content */}
            <Container fluid>
                {/* Welcome Alert */}
                <Alert variant="info" dismissible>
                    <strong>👋 Welcome back!</strong> You have {notifications} new notifications.
                </Alert>

                {/* Stats Cards */}
                <RowView xs={1} sm={2} lg={4} className="gap-1">
                    {stats.map((stat, index) => (
                        <Col key={index}>
                            <CardView
                                bg={stat.bg}
                                text="white"
                                style={{ height: '100%' }}
                            >
                                <Flex justify="space-between" align="center">
                                    <div>
                                        <Text tag="div" style={{ fontSize: '14px', opacity: 0.9 }}>
                                            {stat.title}
                                        </Text>
                                        <Heading level={2} style={{ margin: '10px 0', color: 'white' }}>
                                            {stat.value}
                                        </Heading>
                                        <Badge
                                            bg={stat.changeType === 'positive' ? 'success' : 'danger'}
                                            style={{ fontSize: '12px' }}
                                        >
                                            {stat.change}
                                        </Badge>
                                    </div>
                                    <div style={{ fontSize: '40px', opacity: 0.8 }}>
                                        {stat.icon}
                                    </div>
                                </Flex>
                            </CardView>
                        </Col>
                    ))}
                </RowView>

                <Spacer height="30px" />

                {/* Charts and Progress Section */}
                <RowView>
                    <Col md={8}>
                        <CardView title="Sales Overview" subtitle="Monthly performance">
                            <Spacer height="10px" />
                            
                            {/* Simulated Chart with Progress Bars */}
                            <div>
                                <Flex justify="space-between" align="center">
                                    <Text>January</Text>
                                    <Text>$12,450</Text>
                                </Flex>
                                <Progress now={85} variant="primary" style={{ height: '20px' }} />
                                <Spacer height="15px" />

                                <Flex justify="space-between" align="center">
                                    <Text>February</Text>
                                    <Text>$15,680</Text>
                                </Flex>
                                <Progress now={92} variant="success" style={{ height: '20px' }} />
                                <Spacer height="15px" />

                                <Flex justify="space-between" align="center">
                                    <Text>March</Text>
                                    <Text>$18,230</Text>
                                </Flex>
                                <Progress now={98} variant="info" style={{ height: '20px' }} />
                                <Spacer height="15px" />

                                <Flex justify="space-between" align="center">
                                    <Text>April</Text>
                                    <Text>$11,340</Text>
                                </Flex>
                                <Progress now={70} variant="warning" style={{ height: '20px' }} />
                            </div>

                            <Spacer height="20px" />
                            <Flex gap="10px">
                                <Button variant="primary" size="sm">View Details</Button>
                                <Button variant="outline-secondary" size="sm">Export</Button>
                            </Flex>
                        </CardView>
                    </Col>

                    <Col md={4}>
                        <CardView title="Recent Activity" subtitle="Latest updates">
                            <ListView variant="flush">
                                {activities.map((activity, index) => (
                                    <ListItem key={index}>
                                        <Flex gap="10px" align="start">
                                            <span style={{ fontSize: '20px' }}>{activity.icon}</span>
                                            <div style={{ flex: 1 }}>
                                                <Text tag="div" style={{ fontWeight: '500' }}>
                                                    {activity.text}
                                                </Text>
                                                <Text tag="small" style={{ color: '#6c757d' }}>
                                                    {activity.time}
                                                </Text>
                                            </div>
                                        </Flex>
                                    </ListItem>
                                ))}
                            </ListView>
                            <Spacer height="15px" />
                            <Link to="/activity">View all activity →</Link>
                        </CardView>
                    </Col>
                </RowView>

                <Spacer height="30px" />

                {/* Recent Orders Table */}
                <CardView title="Recent Orders" subtitle="Latest transactions">
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Order ID</th>
                                <th>Customer</th>
                                <th>Product</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentOrders.map((order, index) => (
                                <tr key={index}>
                                    <td><strong>{order.id}</strong></td>
                                    <td>{order.customer}</td>
                                    <td>{order.product}</td>
                                    <td><strong>{order.amount}</strong></td>
                                    <td>
                                        <Badge
                                            bg={
                                                order.status === 'Completed' ? 'success' :
                                                order.status === 'Pending' ? 'warning' :
                                                'info'
                                            }
                                        >
                                            {order.status}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Flex gap="5px">
                                            <Button variant="outline-primary" size="sm">
                                                View
                                            </Button>
                                            <Button variant="outline-secondary" size="sm">
                                                Edit
                                            </Button>
                                        </Flex>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Spacer height="15px" />
                    <Flex justify="space-between" align="center">
                        <Text>Showing 5 of 892 orders</Text>
                        <Button variant="primary">View All Orders</Button>
                    </Flex>
                </CardView>

                <Spacer height="30px" />

                {/* Additional Widgets */}
                <RowView xs={1} md={2} lg={3}>
                    <Col>
                        <CardView title="Quick Actions">
                            <Grid columns="repeat(2, 1fr)" gap="10px">
                                <Button variant="primary" onClick={() => setShowModal(true)}>
                                    ➕ New Order
                                </Button>
                                <Button variant="success">
                                    👤 Add User
                                </Button>
                                <Button variant="info">
                                    📦 Add Product
                                </Button>
                                <Button variant="warning">
                                    📊 Generate Report
                                </Button>
                            </Grid>
                        </CardView>
                    </Col>

                    <Col>
                        <CardView title="System Status">
                            <Flex direction="column" gap="15px">
                                <div>
                                    <Flex justify="space-between" style={{ marginBottom: '10px' }}>
                                        <Text>Server Status</Text>
                                        <Badge bg="success">Online</Badge>
                                    </Flex>
                                    <Progress now={100} variant="success" style={{ height: '5px' }} />
                                </div>

                                <div>
                                    <Flex justify="space-between" style={{ marginBottom: '10px' }}>
                                        <Text>Database</Text>
                                        <Badge bg="success">Connected</Badge>
                                    </Flex>
                                    <Progress now={100} variant="success" style={{ height: '5px' }} />
                                </div>

                                <div>
                                    <Flex justify="space-between" style={{ marginBottom: '10px' }}>
                                        <Text>Storage</Text>
                                        <Badge bg="warning">75%</Badge>
                                    </Flex>
                                    <Progress now={75} variant="warning" style={{ height: '5px' }} />
                                </div>

                                <div>
                                    <Flex justify="space-between" style={{ marginBottom: '10px' }}>
                                        <Text>API Status</Text>
                                        <Badge bg="success">Active</Badge>
                                    </Flex>
                                    <Progress now={100} variant="success" style={{ height: '5px' }} />
                                </div>
                            </Flex>
                        </CardView>
                    </Col>

                    <Col>
                        <CardView title="Support Tickets">
                            <Flex direction="column" gap="15px">
                                <Flex justify="space-between" align="center">
                                    <div>
                                        <Text tag="div" style={{ fontWeight: 'bold' }}>Open</Text>
                                        <Heading level={3} style={{ margin: '5px 0' }}>23</Heading>
                                    </div>
                                    <Badge bg="danger" pill style={{ fontSize: '16px' }}>
                                        High Priority
                                    </Badge>
                                </Flex>

                                <Divider />

                                <Flex justify="space-between">
                                    <Text>In Progress</Text>
                                    <Badge bg="warning">15</Badge>
                                </Flex>

                                <Flex justify="space-between">
                                    <Text>Resolved Today</Text>
                                    <Badge bg="success">42</Badge>
                                </Flex>

                                <Spacer height="10px" />
                                <Button variant="outline-primary" block>
                                    View All Tickets
                                </Button>
                            </Flex>
                        </CardView>
                    </Col>
                </RowView>

                <Spacer height="50px" />
            </Container>

            {/* New Order Modal */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                title="Create New Order"
                size="lg"
                footer={
                    <Flex gap="10px" justify="end">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={() => {
                            alert('Order created!');
                            setShowModal(false);
                        }}>
                            Create Order
                        </Button>
                    </Flex>
                }
            >
                <Input
                    label="Customer Name"
                    type="text"
                    placeholder="Enter customer name"
                    required
                />
                <Input
                    label="Product"
                    type="text"
                    placeholder="Enter product name"
                    required
                />
                <Input
                    label="Quantity"
                    type="number"
                    placeholder="Enter quantity"
                    required
                />
                <Input
                    label="Amount"
                    type="text"
                    placeholder="Enter amount"
                    required
                />
                <Input
                    label="Notes"
                    as="textarea"
                    rows={3}
                    placeholder="Additional notes..."
                />
            </Modal>
        </SidebarLayout>
    );
};

export default DashboardExample;

